import { FormConfiguration } from "../FormConfiguration";
import { FormMode } from "./FormDesignConstants";
import {
    IDesignValidationProvider,
    IRuntimeValidationProvider,
    ValidationIssue,
} from "../../rule-engine-core/Common/IDesignValidationProvider";
import { FormBuilder } from "../FormBuilder";
import { AutoBind } from "../../rule-engine-core/Common/AutoBind";
import { ISampleDataProvider } from "../../rule-engine-core/Common/ISampleDataProvider";
import { IAction } from "../../CommonUI/IAction";
import { FormControl } from "../Controls/FormControl";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { IContextForControl } from "../../rule-engine-core/Common/IContextForControl";
import { IContext } from "../../rule-engine-core/Common/IContext";
import { UIControlRegistration } from "./RegisterUIControl";
import { CONFIG_FORM_KEY } from "./XFormAndLayoutDesignPanel";
import { ControlRenderContext } from "./ControlRenderContext";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { ExecutionData } from "../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";

import { FormConstants } from "./FormConstants";
import { IDesignPanelConfig } from "../../rule-engine-core/Common/IDesignPanelConfig";
import { ReactNode } from "react";

// todo: make this a store
// todo: add other stuff here like data? hidden? disabled?
export class FormContextStore {
    editContext: FormContext;
    controlRenderContexts: { [controlId: string]: ControlRenderContext } = {};
    otherRuntimeIssues: ValidationIssue[] = [];
    otherDesignIssues: ValidationIssue[] = [];

    constructor(editContext: FormContext) {
        this.editContext = editContext;
    }

    getAllRuntimeIssues(): ValidationIssue[] {
        // add up all issues from all controls
        let issues: ValidationIssue[] = [];
        issues.push(...this.otherRuntimeIssues);

        for (let issueList of Object.values(this.controlRenderContexts)) {
            issues.push(...issueList.runtimeIssues);
        }

        return issues;
    }

    getAllDesignIssues(): ValidationIssue[] {
        // add up all issues from all controls
        let issues: ValidationIssue[] = [];
        issues.push(...this.otherDesignIssues);

        for (let issueList of Object.values(this.controlRenderContexts)) {
            issues.push(...issueList.designIssues);
        }

        return issues;
    }

    getControlRenderContext(control: FormControl): ControlRenderContext {
        let retVal = this.controlRenderContexts[control.id];

        if (!retVal) {
            retVal = new ControlRenderContext(control, this.editContext);
            this.controlRenderContexts[control.id] = retVal;
        }

        return retVal;
    }

    getControlRenderContextById(controlId: string, controls: FormControl[]): ControlRenderContext {
        let retVal = this.controlRenderContexts[controlId];

        if (!retVal) {
            // find control based on id
            let control: FormControl | undefined;

            for (let c of controls) {
                if (c.id === controlId) {
                    control = c;
                    break;
                }
            }

            if (control) {
                retVal = new ControlRenderContext(control, this.editContext);
                this.controlRenderContexts[controlId] = retVal;
            } else {
                throw new Error("could not find control");
            }
        }

        return retVal;
    }

    updateValidationIssues(validations: ValidationIssue[], controls: FormControl[], isRuntimeIssues: boolean): void {
        // clear other issues
        if (isRuntimeIssues) {
            this.otherRuntimeIssues.length = 0;
        } else {
            this.otherDesignIssues.length = 0;
        }

        let controlIssues: { [controlId: string]: ValidationIssue[] } = {};

        for (let issue of validations) {
            if (issue.elementId) {
                let issues: ValidationIssue[] = controlIssues[issue.elementId];

                if (!issues) {
                    issues = [];
                    controlIssues[issue.elementId] = issues;
                }

                issues.push(issue);

                continue;
            }

            // do data name checking
            if (issue.dataName) {
                let controlId: string | null = null;

                for (let control of controls) {
                    let dataName = Reflect.get(control, "valueName");

                    if (dataName && dataName === issue.dataName) {
                        controlId = control.id;
                        break;
                    }
                }

                if (controlId) {
                    let issues: ValidationIssue[] = controlIssues[controlId];

                    if (!issues) {
                        issues = [];
                        controlIssues[controlId] = issues;
                    }

                    issues.push(issue);

                    continue;
                }
            }

            // not for a control
            if (isRuntimeIssues) {
                this.otherRuntimeIssues.push(issue);
            } else {
                this.otherDesignIssues.push(issue);
            }
        }

        // map to and ensure not over updating

        for (let control of controls) {
            let controlRenderContext = this.getControlRenderContext(control);

            let issues = controlIssues[controlRenderContext.control.id];

            if (isRuntimeIssues) {
                if (!issues && controlRenderContext.runtimeIssues.length !== 0) {
                    // clear the array
                    controlRenderContext.runtimeIssues.length = 0;
                }

                if (issues) {
                    // todo: we might want to update
                    controlRenderContext.runtimeIssues = issues;
                }
            } else {
                if (!issues && controlRenderContext.designIssues.length !== 0) {
                    // clear the array
                    controlRenderContext.designIssues.length = 0;
                }

                if (issues) {
                    // todo: we might want to update
                    controlRenderContext.designIssues = issues;
                }
            }
        }
    }
}

export class FormContext implements IContext {
    form: FormConfiguration;
    runtimeValidationProvider?: IRuntimeValidationProvider;
    data: ExecutionData = new ExecutionData();
    controlContexts: FormContextStore;
    public onFormDataChange?: (data: ExecutionData) => void;
    // callbacks
    public onFormButtonClick?: (buttonName: string, data: ExecutionData) => void;
    public onFormCancelButton?: () => void;
    public onFormClose?: (data: ExecutionData) => void;
    completeOutcomeName: string | undefined;
    resultPromiseResolve?: (result: string) => void;
    resultPromiseReject?: (error: any) => void;
    resultPromise: Promise<string> = new Promise<string>((resolve, reject) => {
        if (this.completeOutcomeName) {
            resolve(this.completeOutcomeName);
        } else {
            this.resultPromiseResolve = resolve;
            this.resultPromiseReject = reject;
        }
    });
    formTitle?: string;
    designer?: FormBuilder;
    formName?: string;
    showControlBorders: boolean = true;
    expandedConfigPanel: boolean = true;
    isMovingConfigPanel: boolean = false;
    isShowEditLayer: boolean = false;
    selectedId: string | null = null;
    contextControl: IContextForControl | null = null;
    designValidationProvider?: IDesignValidationProvider;
    onFormSave?: (data: any) => void;
    sampleDataProvider?: ISampleDataProvider;
    designConfig?: IDesignPanelConfig;
    clipboardElement: FormControl | null = null;
    deleteControl: FormControl | null = null;
    // called when we force a designer update
    onDesignerUpdate?: () => void;
    topDesignerExtensions?: IAction[];
    bottomDesignerExtensions?: IAction[];

    // ------------------------------------------------
    // -- Designer Context ----------------------------
    // ------------------------------------------------
    private eventLog: ({} | string)[] = [];

    constructor(form: FormConfiguration) {
        this.form = form;
        this.controlContexts = new FormContextStore(this);
        this.computeDesignValidationIssues();
    }

    // runtime
    private _runtimeHideControls: string[] = [];

    get runtimeHideControls() {
        return this._runtimeHideControls;
    }

    set runtimeHideControls(hideControlIds: string[]) {
        const previousHides = [...this._runtimeHideControls];
        const showControls = previousHides.filter((hide) => hideControlIds.indexOf(hide) < 0);
        const newlyHideControls = hideControlIds.filter((hide) => previousHides.indexOf(hide) < 0);
        const updateControls = [...showControls, ...newlyHideControls];
        this._runtimeHideControls = hideControlIds;
        StateManager.propertiesChanged(this, updateControls);
    }

    private _runtimeDisabledControls: string[] = [];

    get runtimeDisabledControls() {
        return this._runtimeDisabledControls;
    }

    set runtimeDisabledControls(disabledControlIds: string[]) {
        const previousHides = [...this._runtimeDisabledControls];
        const enabledControls = previousHides.filter((hide) => disabledControlIds.indexOf(hide) < 0);
        const newlyDisabledControls = disabledControlIds.filter((hide) => previousHides.indexOf(hide) < 0);
        const updateControls = [...enabledControls, ...newlyDisabledControls];
        this._runtimeDisabledControls = disabledControlIds;
        StateManager.propertiesChanged(this, updateControls);
    }

    // this should ONLY be in designer.  Not here.
    private _mode: FormMode | string = FormMode.FormDesign;

    get mode(): FormMode | string {
        return this._mode;
    }

    set mode(value: FormMode | string) {
        this._mode = value;

        if (value === FormMode.Runtime) this.computeRuntimeValidations();
    }

    private _lastSelectionElement: string | null = null;

    get lastSelectionElement(): string | null {
        return this._lastSelectionElement;
    }

    set lastSelectionElement(id: string | null) {
        this._lastSelectionElement = id;
    }

    completeFormWithOutcome(outcomename: string) {
        this.completeOutcomeName = outcomename;
        this.data.datalocked = true;

        if (this.resultPromiseResolve) {
            this.resultPromiseResolve(outcomename);
        }
        StateManager.propertyChanged(this, "completeOutcomeName");
    }

    @AutoBind
    outcomeTriggered(name: string) {
        if (this.onFormButtonClick) this.onFormButtonClick(name, this.data);

        this.completeFormWithOutcome(name);
    }

    onClose() {
        if (this.onFormClose) {
            this.onFormClose(this.data);
        }
        this.completeFormWithOutcome(FormConstants.FORM_CLOSED_RESULT);
    }

    onCancel() {
        if (this.onFormCancelButton) {
            this.onFormCancelButton();
        }
        this.completeFormWithOutcome(FormConstants.FORM_CANCELED_RESULT);
    }

    getControlContext(control: FormControl): ControlRenderContext {
        return this.controlContexts.getControlRenderContext(control);
    }

    computeRuntimeValidations(fieldName: string = ""): ValidationIssue[] {
        let breaks: ValidationIssue[];
        breaks = [];

        for (let c of this.form.getAllControls()) {
            let results = c.getRuntimeValidationIssues();
            if (results) {
                breaks.push(...results);
            }
        }

        // call validation provider if it exists for additional validation
        let valBreaks = this.runtimeValidationProvider?.getRuntimeValidationIssues();
        if (valBreaks) {
            breaks.push(...valBreaks);
        }

        // todo: call built in rules
        if (fieldName) {
            const ruleBreaks = this.form.getRuleValidationIssues(fieldName);
            if (ruleBreaks) {
                breaks.push(...ruleBreaks);
            }
        }

        this.controlContexts.updateValidationIssues(breaks, this.form.getAllControls(), true);

        return breaks;
    }

    setFormDataValue(fieldName: string, value: any): void {
        const currentValue = this.getFormDataValue(fieldName);

        if (value === currentValue) return;

        this.data.values = {
            ...this.data.values,
            [fieldName]: value,
        };

        // this.refreshUserForm();

        if (this.onFormDataChange) {
            this.onFormDataChange(this.data);
        }

        this.computeRuntimeValidations(fieldName);
        this.form.dataChangeByRule(fieldName);
        this.form.hideControlByRule();
        this.form.disableControlByRule();
        this.form.focusControlByRule();
        StateManager.propertyChanged(this, fieldName);

        if (this.onFormDataChange) {
            this.onFormDataChange(this.data);
        }
    }

    getFormDataValue(fieldName: string): any {
        if (!this.data.values) return;

        return this.data.values[fieldName];
    }

    setFormData(data: ExecutionData | object): void {
        if (data instanceof ExecutionData) {
            this.data = data;
        } else {
            this.data.values = data;
        }
        this.computeRuntimeValidations();
    }

    getFormData(): ExecutionData {
        return this.data;
    }

    @AutoBind
    cloneControl(control: FormControl): FormControl {
        let type = Reflect.get(control, "__type");
        let registeredControl = UIControlRegistration[type];
        let val = new registeredControl.prototype.constructor();

        return Object.assign(val, control);
    }

    @AutoBind
    onCopy(id?: string) {
        let controlId = id ? id : this.lastSelectionElement;
        if (!controlId) return;

        let control = this.form.find(controlId);
        if (!control) return;

        this.clipboardElement = this.cloneControl(control);

        if (id) this.unselectContextControl();

        StateManager.changed(this);
    }

    @AutoBind
    onCut(id?: string) {
        let controlId = id ? id : this.lastSelectionElement;
        if (!controlId) return;

        let control = this.form.find(controlId);
        if (!control) return;

        this.clipboardElement = this.cloneControl(control);

        this.unSelectControl();
        this.form.remove(control);
        if (id) this.unselectContextControl();

        StateManager.changed(this);
    }

    @AutoBind
    onDelete(id?: string) {
        let controlId = id ? id : this.lastSelectionElement;
        if (!controlId) return;

        let control = this.form.find(controlId);

        if (!control) return;
        this.deleteControl = control;
        if (id) this.unselectContextControl();
        StateManager.changed(this);
    }

    @AutoBind
    onPaste(id?: string) {
        let controlId = id || this.lastSelectionElement;
        if (this.clipboardElement && controlId) {
            this.clipboardElement.id = DataUtilities.generateUniqueId();
            let control = this.form.find(controlId);
            this.form.getContainers().forEach((c) => {
                let index = c.getControls().indexOf(control!);
                if (index >= 0) {
                    c.add(this.cloneControl(this.clipboardElement!), index + 1);
                }
            });
            if (id) this.unselectContextControl();

            StateManager.changed(this);
        }
    }

    addToEventLog(event: {} | string) {
        this.eventLog.push(event);
        // make sure does not grow too big
        if (this.eventLog.length > 100) this.eventLog.shift();
    }

    getEventLog(): ({} | string)[] {
        return this.eventLog;
    }

    computeDesignValidationIssues(): ValidationIssue[] {
        let breaks: ValidationIssue[];
        breaks = [];
        // value name is filled in (call data class)
        // value name cannot have special characters or space
        // buttons have names (call button class)

        for (let c of this.form.getContainers()) {
            let results = c.getDesignValidationIssues();
            if (results && results.length > 0) {
                breaks.push(...results);
            }
        }

        for (let c of this.form.getAllControls()) {
            let results = c.getDesignValidationIssues();
            if (results) {
                breaks.push(...results);
            }
        }

        // call validation provider if it exists for additional validation
        let valBreaks = this.designValidationProvider?.getDesignValidationIssues();
        if (valBreaks) {
            breaks.push(...valBreaks);
        }

        this.controlContexts.updateValidationIssues(breaks, this.form.getAllControls(), false);
        StateManager.propertyChanged(this.controlContexts, "validationIssues");
        return breaks;
    }

    @AutoBind
    refreshDesigner(): void {
        this.designer?.forceUpdate();

        if (this.onDesignerUpdate) {
            this.onDesignerUpdate();
        }

        this.computeDesignValidationIssues();
    }

    getEditUI(defaultId?: string): ReactNode {
        const selectedId = this.selectedId || defaultId;

        if (!selectedId) return;

        if (selectedId === CONFIG_FORM_KEY) {
            return this.form.renderEditUI();
        }

        let control = this.form.find(selectedId);

        if (control) return control.renderEditUI();
    }

    getSelectedId(): string | null {
        return this.selectedId;
    }

    getSelectedControl(defaultControlId?: string): FormControl | null {
        const selectedId = this.selectedId || defaultControlId;
        if (selectedId) {
            return this.form.find(selectedId);
        }
        return null;
    }

    @AutoBind
    selectControl(selectedId: string) {
        this.unSelectControl();

        this.selectedId = selectedId;
        this.lastSelectionElement = selectedId;

        if (selectedId !== CONFIG_FORM_KEY && this.form.find(selectedId)) {
            let cc = this.controlContexts.getControlRenderContextById(selectedId, this.form.getAllControls());
            cc.setSelected(true);
        }

        this.isShowEditLayer = true;

        StateManager.changed(this);
    }

    @AutoBind
    unSelectControl() {
        if (this.selectedId) {
            if (this.selectedId !== CONFIG_FORM_KEY && this.form.find(this.selectedId)) {
                let cc = this.controlContexts.getControlRenderContextById(this.selectedId, this.form.getAllControls());
                cc.setSelected(false);
            }
        }

        this.selectedId = null;

        StateManager.changed(this);
    }

    @AutoBind
    hideEditLayer() {
        this.isShowEditLayer = false;
        StateManager.changed(this);
    }

    @AutoBind
    showEditLayer() {
        this.isShowEditLayer = true;
        StateManager.changed(this);
    }

    @AutoBind
    toggleEditLayer() {
        this.isShowEditLayer = !this.isShowEditLayer;
        StateManager.changed(this);
    }

    @AutoBind
    selectContextControl(control: IContextForControl) {
        this.contextControl = control;
        StateManager.changed(this);
    }

    @AutoBind
    unselectContextControl() {
        this.contextControl = null;
        StateManager.changed(this);
    }

    @AutoBind
    refreshUserForm(): void {
        this.refreshDesigner();
        // todo: refresh user form
    }
}
